"use client";

import type { CSSProperties } from "react";
import clsx from "clsx";
import Lottie from "lottie-react";

// Imported once at module scope so the parsed animation data keeps a stable
// reference across renders — lottie-react re-initialises the animation when
// `animationData` changes identity, so this avoids recreating it every render.
import loaderAnimation from "./loader-animation.json";

type LottieLoaderProps = {
  label?: string;
  size?: number;
  className?: string;
  centered?: boolean;
  fullScreen?: boolean;
};

/**
 * Application-wide loading indicator, rendered as a looping Lottie animation.
 * Drop-in replacement for the former CSS spinner — same props and the same
 * `role="status"` / `aria-busy` / screen-reader-label semantics.
 */
export default function LottieLoader({
  label = "Loading...",
  size = 150,
  className,
  centered = false,
  fullScreen = false,
}: LottieLoaderProps) {
  return (
    <div
      aria-live="polite"
      aria-busy="true"
      role="status"
      className={clsx(
        "inline-flex items-center",
        centered && "justify-center w-full",
        fullScreen && "min-h-[40vh] w-full",
        className
      )}
    >
      <Lottie
        animationData={loaderAnimation}
        loop
        autoplay
        aria-hidden="true"
        rendererSettings={{ preserveAspectRatio: "xMidYMid meet" }}
        style={
          {
            width: size,
            height: size,
            flex: "0 0 auto",
          } as CSSProperties
        }
      />
      {label ? <span className="sr-only">{label}</span> : null}
    </div>
  );
}
